import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "@emotion/styled";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import Spinner from "@components/Spinner";
import {
  CalendarDropdown,
  DateRow,
  DAY,
  HOUR,
  LoadingWrap,
  MetricsHeader,
  MetricsShell,
  MetricsSub,
  MetricsTitle,
  MINUTE,
  Panel,
  PanelTitle,
  PanelTitleRow,
  ResetZoomBtn,
  HintText,
  TimeRangeDropdown,
  type TimeRangePreset,
  fmtLocalDatetime,
  percentileDomain,
  toISO,
  tooltipStyle,
  useChartZoom,
} from "@components/DeviceMetrics";
import {
  deviceTrackDays,
  deviceTrackMetrics,
  type TTrackMetricsResponse,
} from "@services/devicesService";
import { useLang } from "@src/hooks/useLang";
import useIsMobile, { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

// ── Constants ──────────────────────────────────────────────

/**
 * The widest window one request may cover, matching the API. Presets stop here
 * rather than offering "Last 7 days" and letting the request 400 — a wider
 * window is a proportionally larger `tracks` scan, so the UI states the limit
 * instead of discovering it.
 *
 * Note this bounds WIDTH, not recency: the calendar can reach any day the
 * device still has points for, because a 24 h window six months back costs the
 * same as one from yesterday.
 */
const MAX_WINDOW_MS = 2 * DAY;

const PRESETS: TimeRangePreset[] = [
  { label: "Last 5 minutes", ms: 5 * MINUTE },
  { label: "Last 15 minutes", ms: 15 * MINUTE },
  { label: "Last 1 hour", ms: HOUR },
  { label: "Last 3 hours", ms: 3 * HOUR },
  { label: "Last 6 hours", ms: 6 * HOUR },
  { label: "Last 12 hours", ms: 12 * HOUR },
  { label: "Last 24 hours", ms: 24 * HOUR },
  { label: "Last 2 days", ms: MAX_WINDOW_MS },
];

const SENSOR_COLOR = "#73bf69";
const FREQ_COLOR = "#eab839";
const SAT_COLOR = "#5794f2";

// ── Styled ─────────────────────────────────────────────────

const Page = styled.div`
  min-height: 100vh;
  background: #0b0c0f;
  padding: 20px 20px 48px;
  font-family: Nunito Sans, sans-serif;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 12px 12px 32px;
  }
`;

const TopBar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const RefreshBtn = styled(ResetZoomBtn)`
  height: 36px;
  padding: 0 14px;
`;

// Groups the two pickers on the right. `> *` neutralises the `margin-left:auto`
// that TimeRangeDropdownWrap carries for its original single-control layout —
// left in place it would shove the range picker away from the calendar instead
// of keeping the pair together.
const ToolbarRight = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  > * {
    margin-left: 0;
  }
`;

const Notice = styled.div`
  max-width: 520px;
  margin: 64px auto;
  padding: 24px;
  border-radius: 8px;
  background: #111217;
  border: 1px solid #2a2e37;
  color: #c7d0d9;
  font-size: 14px;
  text-align: center;
`;

// ── Helpers ────────────────────────────────────────────────

/**
 * One bucket, as the charts consume it.
 *
 * The bucket MEAN is deliberately absent. This page is a diagnostic: every
 * value it draws has to be a reading the device actually produced, and a mean
 * over a 17 s bucket is not one — it flattens the narrow spikes that are the
 * whole reason someone opens this screen. So each panel plots the bucket's own
 * `min` and `max` as two traces. When the window is narrow enough that
 * the server floors the bucket at 1 s, min and max collapse onto the same raw
 * sample and the pair renders as a single unsmoothed trace.
 */
type ChartRow = {
  /** Unix seconds — a numeric X axis, so every panel shares one domain. */
  time: number;
  valueMin: number | null;
  valueMax: number | null;
  freqMin: number | null;
  freqMax: number | null;
  satMin: number | null;
  satMax: number | null;
};


function fmtClock(unixSec: number): string {
  const d = new Date(unixSec * 1000);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getHours())}:${p(d.getMinutes())}`;
}

function fmtBucket(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

// ── Screen ─────────────────────────────────────────────────

/**
 * Customer-facing device telemetry — the same view we use internally, minus the
 * admin-only panels. Opened in its own tab from the Devices list, so it renders
 * without the app chrome.
 *
 * Deliberately NOT auto-refreshing. Devices report on a 10-minute MQTT flush, so
 * a ticking chart would re-read `tracks` for data that has not changed and hold
 * the database compute awake — which is what the bill is actually made of.
 */
const DeviceTechnicalScreen: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const lang = useLang();
  // Drag-to-zoom is pointer-only (see useChartZoom). On a phone the calendar
  // and the range picker in the toolbar are the only way to change the window.
  const isMobile = useIsMobile();
  const t = (lang.devices ?? {}) as any;

  const [from, setFrom] = useState(() =>
    fmtLocalDatetime(new Date(Date.now() - 24 * HOUR))
  );
  const [to, setTo] = useState(() => fmtLocalDatetime(new Date()));
  const [selectedPresetMs, setSelectedPresetMs] = useState<number | null>(24 * HOUR);
  const [data, setData] = useState<TTrackMetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<{ message: string; notAvailable: boolean } | null>(
    null
  );
  /** Ranges we zoomed out of, so "Zoom out" walks back one step at a time. */
  const [history, setHistory] = useState<Array<{ from: string; to: string }>>([]);
  /** Day picker: dotted days for the visible month, plus the plottable bounds. */
  const [visibleMonth, setVisibleMonth] = useState<Date>(() => new Date());
  const [daysWithData, setDaysWithData] = useState<Date[]>([]);
  const [bounds, setBounds] = useState<{ earliest: Date | null; latest: Date | null }>(
    { earliest: null, latest: null }
  );
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // Latest fetchData call: zooming or picking another day while a long range
  // is still loading must not let the older response land last.
  const fetchReqRef = useRef(0);

  const fetchData = useCallback(async () => {
    const req = ++fetchReqRef.current;
    setLoading(true);
    setError(null);
    try {
      const next = await deviceTrackMetrics(id, new Date(toISO(from)), new Date(toISO(to)));
      if (req === fetchReqRef.current) setData(next);
    } catch (e: any) {
      if (req !== fetchReqRef.current) return;
      // A 404 here is the potato-only rule, not a missing device: the button is
      // hidden for other machines, so reaching this means a hand-typed URL.
      setError({
        message: e?.message ?? "Failed to load",
        notAvailable: e?.statusCode === 404,
      });
      setData(null);
    } finally {
      if (req === fetchReqRef.current) setLoading(false);
    }
  }, [id, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // One request per visible month, in the browser's own timezone so the dots
  // agree with the Tracks and Analytics calendars. Cheap by construction (a
  // per-day index walk, not a scan), which is what makes paging months viable.
  useEffect(() => {
    let cancelled = false;
    const monthStart = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth(),
      1
    );
    const monthEnd = new Date(
      visibleMonth.getFullYear(),
      visibleMonth.getMonth() + 1,
      0
    );
    const iso = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`;
    deviceTrackDays(id, iso(monthStart), iso(monthEnd))
      .then((r) => {
        if (cancelled) return;
        // The server already cut these at local midnight, so parse them as
        // LOCAL dates too — `new Date("2026-08-24")` would read them back as UTC
        // and undo that. Same handling deviceTrackDates uses for Tracks.
        setDaysWithData(
          r.days.map((d) => {
            const [y, m, day] = d.split("-").map(Number);
            return new Date(y, m - 1, day);
          })
        );
        setBounds({
          earliest: r.earliest ? new Date(r.earliest) : null,
          latest: r.latest ? new Date(r.latest) : null,
        });
      })
      .catch(() => {
        // A calendar without dots still picks days; failing loudly here would
        // bury the charts behind an error they do not depend on.
        if (!cancelled) setDaysWithData([]);
      });
    return () => {
      cancelled = true;
    };
  }, [id, visibleMonth]);

  useEffect(() => {
    document.title = data?.device
      ? `${data.device.name} — ${t.technicalStatusTitle ?? "Technical status"}`
      : (t.technicalStatusTitle ?? "Technical status");
  }, [data, t.technicalStatusTitle]);

  const handleSelectPreset = useCallback((ms: number) => {
    const now = new Date();
    setFrom(fmtLocalDatetime(new Date(now.getTime() - ms)));
    setTo(fmtLocalDatetime(now));
    setSelectedPresetMs(ms);
    setSelectedDay(null);
    setHistory([]);
  }, []);

  /**
   * A picked day is local midnight to local midnight — 24 h, comfortably inside
   * the 48 h width cap. Clamped to `now` for today, so "today" plots what exists
   * rather than padding the axis with hours that have not happened.
   */
  const handleSelectDay = useCallback((d: Date | null) => {
    if (!d) return;
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const end = new Date(start.getTime() + 24 * HOUR);
    const now = new Date();
    setSelectedDay(start);
    setFrom(fmtLocalDatetime(start));
    setTo(fmtLocalDatetime(end > now ? now : end));
    setSelectedPresetMs(null);
    setHistory([]);
  }, []);

  const handleApplyAbsolute = useCallback((newFrom: string, newTo: string) => {
    setFrom(newFrom);
    setTo(newTo);
    setSelectedPresetMs(null);
    setHistory([]);
  }, []);

  const chartData: ChartRow[] = useMemo(() => {
    if (!data) return [];
    return data.points.map((p) => ({
      time: new Date(p.time).getTime() / 1000,
      valueMin: p.valueMin,
      valueMax: p.valueMax,
      freqMin: p.freqMin,
      freqMax: p.freqMax,
      satMin: p.satellitesMin,
      satMax: p.satellitesMax,
    }));
  }, [data]);

  /**
   * Zooming refetches instead of clipping the loaded series. That is the whole
   * point of server-side bucketing: a narrower window means a narrower bucket,
   * so detail genuinely appears rather than the same points being stretched.
   */
  const handleZoom = useCallback(
    (t1: number, t2: number) => {
      setHistory((h) => [...h, { from, to }]);
      setFrom(fmtLocalDatetime(new Date(t1 * 1000)));
      setTo(fmtLocalDatetime(new Date(t2 * 1000)));
      setSelectedPresetMs(null);
    },
    [from, to]
  );

  const { handlers, selection } = useChartZoom(chartData, handleZoom, !isMobile);

  const zoomOut = useCallback(() => {
    setHistory((h) => {
      const prev = h[h.length - 1];
      if (prev) {
        setFrom(prev.from);
        setTo(prev.to);
      }
      return h.slice(0, -1);
    });
  }, []);

  const xDomain = useMemo((): [number, number] | undefined => {
    if (chartData.length === 0) return undefined;
    const min = chartData[0].time;
    const max = chartData[chartData.length - 1].time;
    const pad = Math.max((max - min) * 0.02, 1);
    return [min - pad, max + pad];
  }, [chartData]);

  /**
   * Fed the mins AND the maxes, because both are drawn now. Scaling to the
   * means would put the max trace outside the axis and clip it off.
   *
   * The 1st/99th-percentile clip stays: that is axis framing, not smoothing —
   * it stops one absurd reading from squashing the whole trace flat, and the
   * reading itself is still plotted, just outside the visible band.
   */
  const valueDomain = useMemo(
    () =>
      percentileDomain(
        chartData
          .flatMap((r) => [r.valueMin, r.valueMax])
          .filter((v): v is number => v != null)
      ),
    [chartData]
  );
  /**
   * NOT percentileDomain, unlike the other two. A dropout is rare by definition
   * — one bucket in 1300 on a real device — so a 1st-percentile clip would trim
   * away the very event this panel exists to show, and an axis that starts above
   * zero makes a total loss of fix look like a mild dip. Always anchored at 0.
   */
  const satDomain = useMemo((): [number, number] => {
    const highs = chartData
      .map((r) => r.satMax)
      .filter((v): v is number => v != null);
    return [0, highs.length ? Math.max(...highs) + 2 : 24];
  }, [chartData]);

  const freqDomain = useMemo(
    () =>
      percentileDomain(
        chartData
          .flatMap((r) => [r.freqMin, r.freqMax])
          .filter((v): v is number => v != null)
      ),
    [chartData]
  );



  // Was `now - 48 h`, which made sense when recency was capped. It is the WIDTH
  // that is capped now, so the absolute inputs should reach back as far as the
  // device actually has points — anything less would contradict the calendar
  // sitting right beside them.
  const minDate = bounds.earliest ? fmtLocalDatetime(bounds.earliest) : undefined;

  const renderChart = (
    title: string,
    minKey: "valueMin" | "freqMin" | "satMin",
    maxKey: "valueMax" | "freqMax" | "satMax",
    color: string,
    domain: [number, number],
    threshold?: number | null
  ) => (
    <Panel>
      <PanelTitleRow>
        <PanelTitle>{title}</PanelTitle>
        {!isMobile && (
          <HintText>
            {(t.technicalDragToZoom ?? "Drag across the chart to zoom in")}
          </HintText>
        )}
      </PanelTitleRow>
      <ResponsiveContainer width="100%" height={isMobile ? 200 : 260}>
        <ComposedChart data={chartData} {...handlers}>
          <CartesianGrid stroke="#2a2e37" strokeDasharray="3 3" />
          <XAxis
            dataKey="time"
            type="number"
            domain={xDomain ?? ["dataMin", "dataMax"]}
            tickFormatter={fmtClock}
            stroke="#7b8794"
            fontSize={11}
          />
          <YAxis domain={domain} stroke="#7b8794" fontSize={11} width={56} />
          <Tooltip
            contentStyle={tooltipStyle}
            labelFormatter={(v) => new Date(Number(v) * 1000).toLocaleString()}
          />
          {/*
            Stroke both bucket extremes, no fill. A range Area between them drew
            large triangles across sparse stretches, where recharts interpolates
            the band polygon between two distant buckets. `type` is left at
            recharts' `linear` default on purpose, since a `monotone` spline
            would round the peaks back off again.
          */}
          <Line
            dataKey={maxKey}
            stroke={color}
            strokeWidth={1}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
            connectNulls={false}
            name={title}
          />
          <Line
            dataKey={minKey}
            stroke={color}
            strokeWidth={1}
            dot={false}
            activeDot={{ r: 3 }}
            isAnimationActive={false}
            connectNulls={false}
            legendType="none"
            name={title}
          />
          {threshold ? (
            <ReferenceLine
              y={threshold}
              stroke="#e0752d"
              strokeDasharray="4 4"
              strokeOpacity={0.8}
            />
          ) : null}
          {selection()}
        </ComposedChart>
      </ResponsiveContainer>
    </Panel>
  );

  if (error?.notAvailable) {
    return (
      <Page>
        <Notice>
          {t.technicalNotAvailable ??
            "Technical status is not available for this device."}
        </Notice>
      </Page>
    );
  }

  return (
    <Page>
      <TopBar>
        <div>
          <MetricsTitle>
            {data?.device.name ?? (t.technicalStatusTitle ?? "Technical status")}
          </MetricsTitle>
          <MetricsSub>
            {data
              ? `${data.device.esn} · ${t.technicalResolution ?? "resolution"} ${fmtBucket(
                  data.bucketSeconds
                )} · ${data.length} ${t.technicalPoints ?? "points"}`
              : id}
          </MetricsSub>
        </div>
      </TopBar>

      <DateRow>
        <RefreshBtn onClick={fetchData} disabled={loading}>
          {t.technicalRefresh ?? "Refresh"}
        </RefreshBtn>
        {!isMobile && history.length > 0 && (
          <ResetZoomBtn onClick={zoomOut}>
            {t.technicalZoomOut ?? "Zoom out"}
          </ResetZoomBtn>
        )}
        <ToolbarRight>
          <CalendarDropdown
            selected={selectedDay}
            onChange={handleSelectDay}
            datesWithData={daysWithData}
            minDate={bounds.earliest ?? undefined}
            maxDate={bounds.latest ?? new Date()}
            onMonthChange={setVisibleMonth}
            placeholder={t.technicalPickDay ?? "Pick a day"}
            hint={
              t.technicalCalendarHint ?? "Dotted days have data. Pick one to plot it."
            }
          />
          <TimeRangeDropdown
            selectedMs={selectedPresetMs}
            from={from}
            to={to}
            onSelectPreset={handleSelectPreset}
            onApplyAbsolute={handleApplyAbsolute}
            presets={PRESETS}
            minDate={minDate}
            labels={{
              quick: t.technicalQuickRanges ?? undefined,
              absolute: t.technicalAbsoluteRange ?? undefined,
              from: t.technicalFrom ?? undefined,
              to: t.technicalTo ?? undefined,
              apply: t.technicalApply ?? undefined,
              custom: t.technicalCustomRange ?? undefined,
            }}
          />
        </ToolbarRight>
      </DateRow>

          <MetricsShell>
            <MetricsHeader>
              <MetricsSub>
                {t.technicalHint ??
                  "Sensor, frequency and satellite count as the machine reported them. Pick a day on the calendar, or a range from the menu."}
              </MetricsSub>
            </MetricsHeader>

            {loading ? (
              <LoadingWrap>
                <Spinner />
              </LoadingWrap>
            ) : error ? (
              <MetricsSub>{error.message}</MetricsSub>
            ) : chartData.length === 0 ? (
              <MetricsSub>
                {t.technicalNoData ?? "No data reported in this period."}
              </MetricsSub>
            ) : (
              <>
                {renderChart(
                  t.technicalSensor ?? "Sensor channel 1",
                  "valueMin",
                  "valueMax",
                  SENSOR_COLOR,
                  valueDomain
                )}
                {renderChart(
                  t.technicalFrequency ?? "Sensor channel 2",
                  "freqMin",
                  "freqMax",
                  FREQ_COLOR,
                  freqDomain
                )}
                {renderChart(
                  t.technicalSatellites ?? "Satellites",
                  "satMin",
                  "satMax",
                  SAT_COLOR,
                  satDomain,
                  // The device drops fixes below this many satellites, so it is the
                  // line that decides whether a dip actually cost anything. Only
                  // drawn when the device sets one — 0 means "keep every fix".
                  data?.device.minSatellitesCount || null
                )}
              </>
            )}
          </MetricsShell>
    </Page>
  );
};

export default DeviceTechnicalScreen;
